import { useContext, useMemo, useState } from "react";
import { db, timestamp } from "../../../firebase/config";
import { client } from "../../../hooks/Client";
import { SavedIcon } from "../../../StateManagment/SavedIcon";
import { useFirestoreGeneralOrderBy } from "../../../firebase/useFirestore";
import uuid from "react-uuid";
import Tooltip from "../../common/Tooltip";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import OptionsModalContent from "./OptionsModalContent";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import getColumnType from "../../../helpers/intakeForms/getColumnType";

// Builder for a matrix (table) intake field: the admin defines the rows and the
// columns, and each column carries its own answer type.
//
// Rows and columns reuse the questionnaire's MatrixQuestionRows /
// MatrixQuestionColumns collections (both keyed by a generic `Field` FK), so no
// new Firestore collection — and therefore no new security rule — is needed.
//
// AI-CONTEXT: See docs/features/matrix-field-intakeform.md
const IntakeFormMatrixEditor = ({ field }) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon);

    // State — which 'choice' column is having its answer options edited
    const [optionsColumn, setOptionsColumn] = useState(null);

    // Firestore — the hooks return soft-deleted docs too, so they are filtered
    // here, the same way OptionsModalContent does. There is no central filter.
    const fieldId = field?.ID || "";
    const rawRows = useFirestoreGeneralOrderBy(
        "MatrixQuestionRows",
        "Field",
        fieldId,
        "Position",
        "asc"
    );
    const rawColumns = useFirestoreGeneralOrderBy(
        "MatrixQuestionColumns",
        "Field",
        fieldId,
        "Position",
        "asc"
    );

    const notDeleted = (d) => (d?.Deleted || "false") !== "true";

    const rows = useMemo(() => (rawRows || []).filter(notDeleted), [rawRows]);
    const columns = useMemo(
        () => (rawColumns || []).filter(notDeleted),
        [rawColumns]
    );

    const baseDoc = (position) => ({
        ID: uuid(),
        Title: "",
        Field: fieldId,
        Compagny: client,
        CompagnyID: client,
        Position: position,
        Timestamp: timestamp,
        Deleted: "false",
        DeletedAt: "",
    });

    // Position counts soft-deleted docs too, so a delete-then-add cannot reuse a
    // Position that is still taken by a living row.
    const addRow = async () => {
        if (!fieldId) return;

        await db
            .collection("MatrixQuestionRows")
            .doc()
            .set(baseDoc((rawRows?.length || 0) + 1));

        setSaved("flex");
    };

    // A new column is explicitly 'text' — the friendliest default for a data
    // grid. (An absent Type means 'choice', but that is only for the legacy
    // questionnaire columns, which predate this property.)
    const addColumn = async () => {
        if (!fieldId) return;

        await db
            .collection("MatrixQuestionColumns")
            .doc()
            .set({ ...baseDoc((rawColumns?.length || 0) + 1), Type: "text" });

        setSaved("flex");
    };

    const updateDoc = async (collection, docid, values) => {
        if (!docid) return;

        await db.collection(collection).doc(docid).update(values);
        setSaved("flex");
    };

    // Soft delete only — ADR-008. Answers already given for this row/column stay
    // readable; they just stop being asked for.
    const softDelete = (collection) => async (e) => {
        const docid = e.currentTarget.dataset.docid;

        await updateDoc(collection, docid, {
            Deleted: "true",
            DeletedAt: timestamp,
        });
    };

    const titleHandler = (collection) => (e) =>
        updateDoc(collection, e.target.dataset.docid, { Title: e.target.value });

    const columnTypeHandler = (e) =>
        updateDoc("MatrixQuestionColumns", e.target.dataset.docid, {
            Type: e.target.value,
        });

    // A read-only preview of what a respondent will see in a cell of this column.
    // Disabled on purpose — the builder configures structure, not answers.
    const previewCell = (column) => {
        const type = getColumnType(column);

        if (type === "choice") {
            return (
                <select disabled>
                    <option>Keuzelijst…</option>
                </select>
            );
        }

        return (
            <input
                type={type === "number" ? "number" : "text"}
                disabled
                placeholder={type === "number" ? "Getal" : "Tekst"}
            />
        );
    };

    if (!fieldId) return null;

    // Editing the answer options of one 'choice' column. Rendered in place rather
    // than in a nested modal.
    if (optionsColumn) {
        return (
            <div className="matrix-editor">
                <div className="matrix-editor-back">
                    <Tooltip content="Terug naar de matrix" top="-60px" width={"90px"}>
                        <ArrowBackOutlinedIcon
                            className="table-delete-icon"
                            onClick={() => setOptionsColumn(null)}
                        />
                    </Tooltip>
                    <h3>Antwoordopties: {optionsColumn.Title || "Naamloze kolom"}</h3>
                </div>
                <OptionsModalContent field={optionsColumn} />
            </div>
        );
    }

    return (
        <ReadOnlyGuard edit="forms.edit">
            {(canEdit) => (
                <div className="intake-matrix-editor">
                    {canEdit && (
                        <div className="add-matrix-container">
                            <button
                                type="button"
                                className="intake-matrix-add"
                                onClick={addColumn}
                            >
                                <AddOutlinedIcon /> Kolom toevoegen
                            </button>
                            <button
                                type="button"
                                className="intake-matrix-add"
                                onClick={addRow}
                            >
                                <AddOutlinedIcon /> Rij toevoegen
                            </button>
                        </div>
                    )}

                    {columns.length === 0 && rows.length === 0 ? (
                        <p className="matrix-editor-empty">
                            Voeg een kolom en een rij toe om de tabel op te bouwen.
                        </p>
                    ) : (
                        <div className="intake-matrix-scroll">
                            <div
                                className="intake-matrix-grid intake-matrix-grid-builder"
                                style={{
                                    gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${columns.length}, minmax(190px, 1fr))`,
                                }}
                            >
                                {/* top-left corner */}
                                <div className="matrix-grid-header" />

                                {/* one config header per column */}
                                {columns.map((column) => (
                                    <div
                                        key={column.ID}
                                        className="matrix-grid-header intake-matrix-col-config"
                                    >
                                        <input
                                            type="text"
                                            data-docid={column.docid}
                                            defaultValue={column.Title}
                                            placeholder="Naam van kolom"
                                            disabled={!canEdit}
                                            onBlur={titleHandler("MatrixQuestionColumns")}
                                        />

                                        <select
                                            data-docid={column.docid}
                                            value={getColumnType(column)}
                                            disabled={!canEdit}
                                            onChange={columnTypeHandler}
                                        >
                                            <option value="text">Tekst</option>
                                            <option value="number">Getal</option>
                                            <option value="choice">Keuzelijst</option>
                                        </select>

                                        <div className="intake-matrix-col-actions">
                                            {getColumnType(column) === "choice" && (
                                                <Tooltip
                                                    content="Antwoordopties aanpassen"
                                                    top="-60px"
                                                    width={"90px"}
                                                >
                                                    <EditOutlinedIcon
                                                        className="matrix-delete-icon"
                                                        onClick={() => setOptionsColumn(column)}
                                                    />
                                                </Tooltip>
                                            )}

                                            {canEdit && (
                                                <Tooltip
                                                    content="Kolom verwijderen"
                                                    top="-60px"
                                                    width={"90px"}
                                                >
                                                    <DeleteOutlinedIcon
                                                        className="matrix-delete-icon"
                                                        data-docid={column.docid}
                                                        onClick={softDelete("MatrixQuestionColumns")}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* one row = a config header + a preview cell per column */}
                                {rows.map((row) => (
                                    <div key={row.ID} className="intake-matrix-row">
                                        <div className="matrix-grid-header first-column intake-matrix-row-config">
                                            <input
                                                type="text"
                                                data-docid={row.docid}
                                                defaultValue={row.Title}
                                                placeholder="Naam van rij"
                                                disabled={!canEdit}
                                                onBlur={titleHandler("MatrixQuestionRows")}
                                            />

                                            {canEdit && (
                                                <Tooltip
                                                    content="Rij verwijderen"
                                                    top="-60px"
                                                    width={"90px"}
                                                >
                                                    <DeleteOutlinedIcon
                                                        className="matrix-delete-icon"
                                                        data-docid={row.docid}
                                                        onClick={softDelete("MatrixQuestionRows")}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>

                                        {columns.map((column) => (
                                            <div key={column.ID} className="matrix-grid-item">
                                                {previewCell(column)}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ReadOnlyGuard>
    );
};

export default IntakeFormMatrixEditor;
